import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import {
    BenchmarkRequestError, FieldMappingsResponse, MappingRelation, benchmarkService
} from '../../../services/benchmarkService';
import { dsVar, ink } from '../../../theme/tokens';

type Decision = { action: 'confirm' | 'reject'; target: string };

/**
 * Confirm the columns the model identified for required fields.
 *
 * A wrong mapping of one of these gives wrong results with nothing on the page
 * to show it, so the loader will not use one until a person has said it is
 * right. The confirmation is saved for the organisation: every later file with
 * these column names is read the same way.
 */
const ConfirmColumns: React.FC<{
    relation: MappingRelation;
    onConfirmed: () => void;
    onCancel: () => void;
}> = ({ relation, onConfirmed, onCancel }) => {
    const [mappings, setMappings] = useState<FieldMappingsResponse | null>(null);
    const [decisions, setDecisions] = useState<Record<string, Decision>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        benchmarkService.getFieldMappings(relation)
            .then((data) => {
                if (cancelled) return;
                setMappings(data);
                setDecisions(Object.fromEntries(data.pending.map((p) => [
                    p.source, { action: 'confirm', target: p.targetColumn ?? '' }
                ])));
            })
            .catch((err) => {
                if (!cancelled) setError(err instanceof BenchmarkRequestError ? err.message : 'Could not load the columns to confirm.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [relation]);

    const save = async () => {
        const entries = Object.entries(decisions);
        const columns = Object.fromEntries(entries.filter(([, d]) => d.action === 'confirm' && d.target).map(([s, d]) => [s, d.target]));
        const reject = entries.filter(([, d]) => d.action === 'reject').map(([s]) => s);
        setSaving(true);
        setError(null);
        try {
            await benchmarkService.confirmFieldMappings({ relation, columns, reject });
            onConfirmed();
        } catch (err) {
            setError(err instanceof BenchmarkRequestError ? err.message : 'Could not save the confirmations.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box className="flex items-center gap-2" sx={{ py: 3 }}>
                <CircularProgress size={18} sx={{ color: dsVar('accent-fg') }} />
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>Loading the columns to confirm…</Typography>
            </Box>
        );
    }

    const pending = mappings?.pending ?? [];
    const fieldSx = {
        minWidth: 180,
        '& .MuiOutlinedInput-root': {
            backgroundColor: dsVar('page'), color: dsVar('fg-body'), fontSize: '0.8125rem',
            '& fieldset': { borderColor: dsVar('line') }, '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') }
        },
        '& .MuiSvgIcon-root': { color: dsVar('fg-2') }
    };

    return (
        <Box>
            <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-body'), fontWeight: 600 }}>
                Confirm the columns the model identified
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), mt: 0.5, mb: 2, lineHeight: 1.6 }}>
                These columns feed fields the benchmark cannot check for itself, so a person must confirm each one.
                Your answer is saved for your organisation and applies to every later file with these column names.
            </Typography>

            {pending.length === 0 ? (
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>
                    Nothing is waiting for confirmation. Preview the sample again.
                </Typography>
            ) : (
                <Box className="flex flex-col gap-2">
                    {pending.map((p) => {
                        const decision = decisions[p.source];
                        return (
                            <Box key={p.source} className="flex flex-wrap items-center gap-3" sx={{ p: 1.5, borderRadius: '8px', border: `1px solid ${dsVar('line')}` }}>
                                <Box sx={{ minWidth: 160, flex: 1 }}>
                                    <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem', color: dsVar('fg-body') }}>{p.source}</Typography>
                                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                                        Model suggested {p.targetColumn ?? 'nothing'}
                                        {typeof p.confidence === 'number' && ` (${Math.round(p.confidence * 100)}% confident)`}
                                    </Typography>
                                </Box>
                                <TextField
                                    select
                                    size="small"
                                    value={decision?.action === 'reject' ? '__reject__' : decision?.target ?? ''}
                                    onChange={(e) => setDecisions((prev) => ({
                                        ...prev,
                                        [p.source]: e.target.value === '__reject__'
                                            ? { action: 'reject', target: '' }
                                            : { action: 'confirm', target: e.target.value }
                                    }))}
                                    inputProps={{ 'aria-label': `Field for ${p.source}` }}
                                    sx={fieldSx}
                                >
                                    {(mappings?.canonical ?? []).map((field) => (
                                        <MenuItem key={field} value={field}>{field}</MenuItem>
                                    ))}
                                    <MenuItem value="__reject__">Not this — don't use the column</MenuItem>
                                </TextField>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {error && <Typography sx={{ fontSize: '0.8125rem', color: ink('#FCA5A5'), mt: 1.5 }}>{error}</Typography>}

            <Box className="flex justify-end gap-2" sx={{ mt: 2.5 }}>
                <Button onClick={onCancel} sx={{ textTransform: 'none', color: dsVar('fg-2') }}>Back</Button>
                <Button
                    onClick={pending.length ? save : onConfirmed}
                    disabled={saving}
                    variant="contained"
                    sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: dsVar('accent'), color: dsVar('on-accent'), '&:hover': { backgroundColor: '#F5D149' } }}
                >
                    {saving ? 'Saving…' : pending.length ? 'Confirm and preview again' : 'Preview again'}
                </Button>
            </Box>
        </Box>
    );
};

export default ConfirmColumns;
